import { TeamMemberRole } from '@/types/database';

// Define available permissions
export enum Permission {
  // Order management
  VIEW_ORDERS = 'view_orders',
  CREATE_ORDERS = 'create_orders',
  EDIT_ORDERS = 'edit_orders',
  DELETE_ORDERS = 'delete_orders',
  EXPORT_ORDERS = 'export_orders',

  // Inventory management
  VIEW_INVENTORY = 'view_inventory',
  MANAGE_INVENTORY = 'manage_inventory',
  BULK_UPDATE_INVENTORY = 'bulk_update_inventory',
  DELETE_INVENTORY = 'delete_inventory',

  // Event management
  VIEW_EVENTS = 'view_events',
  MANAGE_EVENTS = 'manage_events',
  CREATE_EVENTS = 'create_events',
  DELETE_EVENTS = 'delete_events',

  // Payment management
  VIEW_PAYMENTS = 'view_payments',
  MANAGE_PAYMENTS = 'manage_payments',
  PROCESS_PAYMENTS = 'process_payments',
  VIEW_PAYMENT_HISTORY = 'view_payment_history',

  // Product management
  VIEW_PRODUCTS = 'view_products',
  MANAGE_PRODUCTS = 'manage_products',
  DELETE_PRODUCTS = 'delete_products',

  // Team management
  VIEW_TEAM = 'view_team',
  MANAGE_TEAM = 'manage_team',
  INVITE_TEAM_MEMBERS = 'invite_team_members',
  REMOVE_TEAM_MEMBERS = 'remove_team_members',

  // Settings and configuration
  VIEW_SETTINGS = 'view_settings',
  MANAGE_VENDOR_SETTINGS = 'manage_vendor_settings',
  MANAGE_BILLING = 'manage_billing',

  // Reports and analytics
  VIEW_REPORTS = 'view_reports',
  EXPORT_REPORTS = 'export_reports',

  // Expense and P&L management
  VIEW_EXPENSES = 'view_expenses',
  MANAGE_EXPENSES = 'manage_expenses',
  DELETE_EXPENSES = 'delete_expenses',
  VIEW_PROFIT_LOSS = 'view_profit_loss',
  MANAGE_COST_SETTINGS = 'manage_cost_settings',

  // Dashboard and calendar
  VIEW_DASHBOARD = 'view_dashboard',
  VIEW_CALENDAR = 'view_calendar',

  // Messaging and communications
  VIEW_MESSAGES = 'view_messages',
  MANAGE_MESSAGES = 'manage_messages',
}

// Permission matrix by role
export const ROLE_PERMISSIONS: Record<TeamMemberRole, Permission[]> = {
  admin: [
    // Full access to everything
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_CALENDAR,
    Permission.VIEW_MESSAGES,
    Permission.MANAGE_MESSAGES,
    Permission.VIEW_ORDERS,
    Permission.CREATE_ORDERS,
    Permission.EDIT_ORDERS,
    Permission.DELETE_ORDERS,
    Permission.EXPORT_ORDERS,
    Permission.VIEW_INVENTORY,
    Permission.MANAGE_INVENTORY,
    Permission.BULK_UPDATE_INVENTORY,
    Permission.DELETE_INVENTORY,
    Permission.VIEW_EVENTS,
    Permission.MANAGE_EVENTS,
    Permission.CREATE_EVENTS,
    Permission.DELETE_EVENTS,
    Permission.VIEW_PAYMENTS,
    Permission.MANAGE_PAYMENTS,
    Permission.PROCESS_PAYMENTS,
    Permission.VIEW_PAYMENT_HISTORY,
    Permission.VIEW_PRODUCTS,
    Permission.MANAGE_PRODUCTS,
    Permission.DELETE_PRODUCTS,
    Permission.VIEW_TEAM,
    Permission.MANAGE_TEAM,
    Permission.INVITE_TEAM_MEMBERS,
    Permission.REMOVE_TEAM_MEMBERS,
    Permission.VIEW_SETTINGS,
    Permission.MANAGE_VENDOR_SETTINGS,
    Permission.MANAGE_BILLING,
    Permission.VIEW_REPORTS,
    Permission.EXPORT_REPORTS,
    Permission.VIEW_EXPENSES,
    Permission.MANAGE_EXPENSES,
    Permission.DELETE_EXPENSES,
    Permission.VIEW_PROFIT_LOSS,
    Permission.MANAGE_COST_SETTINGS,
  ],
  manager: [
    // Most features except team management and billing
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_CALENDAR,
    Permission.VIEW_MESSAGES,
    Permission.MANAGE_MESSAGES,
    Permission.VIEW_ORDERS,
    Permission.CREATE_ORDERS,
    Permission.EDIT_ORDERS,
    Permission.EXPORT_ORDERS,
    Permission.VIEW_INVENTORY,
    Permission.MANAGE_INVENTORY,
    Permission.BULK_UPDATE_INVENTORY,
    Permission.VIEW_EVENTS,
    Permission.MANAGE_EVENTS,
    Permission.CREATE_EVENTS,
    Permission.VIEW_PAYMENTS,
    Permission.VIEW_PAYMENT_HISTORY,
    Permission.VIEW_PRODUCTS,
    Permission.MANAGE_PRODUCTS,
    Permission.VIEW_TEAM,
    Permission.VIEW_SETTINGS,
    Permission.VIEW_REPORTS,
    Permission.EXPORT_REPORTS,
    Permission.VIEW_EXPENSES,
    Permission.MANAGE_EXPENSES,
    Permission.VIEW_PROFIT_LOSS,
  ],
  staff: [
    // Read-only access to most features
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_CALENDAR,
    Permission.VIEW_MESSAGES,
    Permission.VIEW_ORDERS,
    Permission.VIEW_INVENTORY,
    Permission.VIEW_EVENTS,
    Permission.VIEW_PRODUCTS,
    Permission.VIEW_REPORTS,
    Permission.VIEW_EXPENSES,
    Permission.VIEW_PROFIT_LOSS,
  ],
};

// Feature-based permission groups for easier checking
export const FEATURE_PERMISSIONS = {
  orders: {
    view: Permission.VIEW_ORDERS,
    create: Permission.CREATE_ORDERS,
    edit: Permission.EDIT_ORDERS,
    delete: Permission.DELETE_ORDERS,
    export: Permission.EXPORT_ORDERS,
  },
  inventory: {
    view: Permission.VIEW_INVENTORY,
    manage: Permission.MANAGE_INVENTORY,
    bulkUpdate: Permission.BULK_UPDATE_INVENTORY,
    delete: Permission.DELETE_INVENTORY,
  },
  events: {
    view: Permission.VIEW_EVENTS,
    manage: Permission.MANAGE_EVENTS,
    create: Permission.CREATE_EVENTS,
    delete: Permission.DELETE_EVENTS,
  },
  payments: {
    view: Permission.VIEW_PAYMENTS,
    manage: Permission.MANAGE_PAYMENTS,
    process: Permission.PROCESS_PAYMENTS,
    history: Permission.VIEW_PAYMENT_HISTORY,
  },
  products: {
    view: Permission.VIEW_PRODUCTS,
    manage: Permission.MANAGE_PRODUCTS,
    delete: Permission.DELETE_PRODUCTS,
  },
  team: {
    view: Permission.VIEW_TEAM,
    manage: Permission.MANAGE_TEAM,
    invite: Permission.INVITE_TEAM_MEMBERS,
    remove: Permission.REMOVE_TEAM_MEMBERS,
  },
  settings: {
    view: Permission.VIEW_SETTINGS,
    manageVendor: Permission.MANAGE_VENDOR_SETTINGS,
    manageBilling: Permission.MANAGE_BILLING,
  },
  reports: {
    view: Permission.VIEW_REPORTS,
    export: Permission.EXPORT_REPORTS,
  },
  messages: {
    view: Permission.VIEW_MESSAGES,
    manage: Permission.MANAGE_MESSAGES,
  },
  expenses: {
    view: Permission.VIEW_EXPENSES,
    manage: Permission.MANAGE_EXPENSES,
    delete: Permission.DELETE_EXPENSES,
  },
  profitLoss: {
    view: Permission.VIEW_PROFIT_LOSS,
    manageCostSettings: Permission.MANAGE_COST_SETTINGS,
  },
};

// Permission checking utilities
export class PermissionChecker {
  private userRole: TeamMemberRole;
  private customPermissions: Permission[];
  private hasCustomOverrides: boolean;

  constructor(userRole: TeamMemberRole, customPermissions: Permission[] = []) {
    this.userRole = userRole;
    // Ensure customPermissions is always a valid array
    this.customPermissions = Array.isArray(customPermissions) ? customPermissions : [];
    this.hasCustomOverrides = this.customPermissions.length > 0;
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: Permission): boolean {
    // If user has custom permission overrides, use those exclusively
    if (this.hasCustomOverrides) {
      // Ensure customPermissions is valid before accessing
      if (!this.customPermissions || !Array.isArray(this.customPermissions)) {
        return false;
      }
      
      const hasDirectPermission = this.customPermissions.includes(permission);
      
      // Check for hierarchical permissions - if user has manage permission, they get view permission
      const hasImpliedPermission = this.getImpliedPermission(permission);
      
      return hasDirectPermission || hasImpliedPermission;
    }

    // Otherwise, fall back to role-based permissions
    const rolePermissions = ROLE_PERMISSIONS[this.userRole] || [];
    return rolePermissions.includes(permission);
  }

  /**
   * Check if user has implied permission based on higher-level permissions
   */
  private getImpliedPermission(permission: Permission): boolean {
    // Ensure custom permissions array exists
    if (!this.customPermissions || !Array.isArray(this.customPermissions)) {
      return false;
    }

    // Map view permissions to their corresponding manage permissions
    // Note: Dashboard and Calendar are NOT included here - they require explicit permission
    const permissionHierarchy: Partial<Record<Permission, Permission[]>> = {
      [Permission.VIEW_ORDERS]: [Permission.CREATE_ORDERS, Permission.EDIT_ORDERS, Permission.DELETE_ORDERS],
      [Permission.VIEW_INVENTORY]: [Permission.MANAGE_INVENTORY, Permission.DELETE_INVENTORY, Permission.BULK_UPDATE_INVENTORY],
      [Permission.VIEW_EVENTS]: [Permission.CREATE_EVENTS, Permission.MANAGE_EVENTS, Permission.DELETE_EVENTS],
      [Permission.VIEW_PAYMENTS]: [Permission.MANAGE_PAYMENTS, Permission.PROCESS_PAYMENTS],
      [Permission.VIEW_PRODUCTS]: [Permission.MANAGE_PRODUCTS, Permission.DELETE_PRODUCTS],
      [Permission.VIEW_SETTINGS]: [Permission.MANAGE_VENDOR_SETTINGS, Permission.MANAGE_BILLING],
      [Permission.VIEW_TEAM]: [Permission.MANAGE_TEAM, Permission.INVITE_TEAM_MEMBERS, Permission.REMOVE_TEAM_MEMBERS],
      [Permission.VIEW_EXPENSES]: [Permission.MANAGE_EXPENSES, Permission.DELETE_EXPENSES],
      [Permission.VIEW_PROFIT_LOSS]: [Permission.MANAGE_COST_SETTINGS],
    };

    const higherPermissions = permissionHierarchy[permission] || [];
    
    // Double-check that customPermissions is still valid before calling .some()
    if (!this.customPermissions || !Array.isArray(this.customPermissions)) {
      return false;
    }
    
    return higherPermissions.some(higherPerm => this.customPermissions.includes(higherPerm));
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: Permission[]): boolean {
    // Ensure permissions array is valid
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
      return false;
    }
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: Permission[]): boolean {
    // Ensure permissions array is valid
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
      return true; // Empty array means "all permissions" is vacuously true
    }
    return permissions.every(permission => this.hasPermission(permission));
  }

  /**
   * Get all permissions for the user
   */
  getAllPermissions(): Permission[] {
    const rolePermissions = ROLE_PERMISSIONS[this.userRole] || [];
    return Array.from(new Set([...rolePermissions, ...this.customPermissions]));
  }

  /**
   * Check feature-level access
   */
  canViewFeature(feature: keyof typeof FEATURE_PERMISSIONS): boolean {
    const featurePerms = FEATURE_PERMISSIONS[feature];
    const viewPermission = featurePerms.view;
    return this.hasPermission(viewPermission);
  }

  /**
   * Check if user can manage a specific feature
   */
  canManageFeature(feature: keyof typeof FEATURE_PERMISSIONS): boolean {
    const featurePerms = FEATURE_PERMISSIONS[feature];
    // Check for manage, create, edit permissions
    const managePermissions = Object.values(featurePerms).filter(
      perm => perm.includes('manage') || perm.includes('create') || perm.includes('edit')
    );
    return this.hasAnyPermission(managePermissions);
  }
}

/**
 * Create permission checker instance
 */
export function createPermissionChecker(
  userRole: TeamMemberRole, 
  customPermissions: Record<string, boolean> = {}
): PermissionChecker {
  // Ensure we have a valid input
  const safeCustomPermissions = customPermissions || {};
  
  // Convert custom permissions object to array if needed
  const customPermArray: Permission[] = Array.isArray(safeCustomPermissions) 
    ? safeCustomPermissions 
    : Object.keys(safeCustomPermissions).filter(key => safeCustomPermissions[key]) as Permission[];

  // Check if there are any custom permissions explicitly set (including false values)
  // This distinguishes between "no custom permissions set" vs "custom permissions set to false"
  const hasExplicitCustomPermissions = safeCustomPermissions && 
    typeof safeCustomPermissions === 'object' && 
    !Array.isArray(safeCustomPermissions) &&
    Object.keys(safeCustomPermissions).length > 0;

  const effectiveCustomPermissions = hasExplicitCustomPermissions ? customPermArray : [];

  // Ensure we always pass a valid array
  const finalPermissions = Array.isArray(effectiveCustomPermissions) ? effectiveCustomPermissions : [];

  return new PermissionChecker(userRole, finalPermissions);
}

/**
 * Utility function to check if a role has permission
 */
export function roleHasPermission(role: TeamMemberRole, permission: Permission): boolean {
  const rolePermissions = ROLE_PERMISSIONS[role] || [];
  return rolePermissions.includes(permission);
}

/**
 * Convert role to default permissions object for database storage
 */
export function getDefaultPermissionsForRole(role: TeamMemberRole): Record<string, boolean> {
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.reduce((acc, permission) => {
    acc[permission] = true;
    return acc;
  }, {} as Record<string, boolean>);
}

/**
 * Get human-readable permission names
 */
export const PERMISSION_NAMES: Record<Permission, string> = {
  [Permission.VIEW_DASHBOARD]: 'View Dashboard',
  [Permission.VIEW_CALENDAR]: 'View Calendar',
  [Permission.VIEW_MESSAGES]: 'View Messages',
  [Permission.MANAGE_MESSAGES]: 'Manage Messages',
  [Permission.VIEW_ORDERS]: 'View Orders',
  [Permission.CREATE_ORDERS]: 'Create Orders',
  [Permission.EDIT_ORDERS]: 'Edit Orders',
  [Permission.DELETE_ORDERS]: 'Delete Orders',
  [Permission.EXPORT_ORDERS]: 'Export Orders',
  [Permission.VIEW_INVENTORY]: 'View Inventory',
  [Permission.MANAGE_INVENTORY]: 'Manage Inventory',
  [Permission.BULK_UPDATE_INVENTORY]: 'Bulk Update Inventory',
  [Permission.DELETE_INVENTORY]: 'Delete Inventory',
  [Permission.VIEW_EVENTS]: 'View Events',
  [Permission.MANAGE_EVENTS]: 'Manage Events',
  [Permission.CREATE_EVENTS]: 'Create Events',
  [Permission.DELETE_EVENTS]: 'Delete Events',
  [Permission.VIEW_PAYMENTS]: 'View Payments',
  [Permission.MANAGE_PAYMENTS]: 'Manage Payments',
  [Permission.PROCESS_PAYMENTS]: 'Process Payments',
  [Permission.VIEW_PAYMENT_HISTORY]: 'View Payment History',
  [Permission.VIEW_PRODUCTS]: 'View Products',
  [Permission.MANAGE_PRODUCTS]: 'Manage Products',
  [Permission.DELETE_PRODUCTS]: 'Delete Products',
  [Permission.VIEW_TEAM]: 'View Team',
  [Permission.MANAGE_TEAM]: 'Manage Team',
  [Permission.INVITE_TEAM_MEMBERS]: 'Invite Team Members',
  [Permission.REMOVE_TEAM_MEMBERS]: 'Remove Team Members',
  [Permission.VIEW_SETTINGS]: 'View Settings',
  [Permission.MANAGE_VENDOR_SETTINGS]: 'Manage Vendor Settings',
  [Permission.MANAGE_BILLING]: 'Manage Billing',
  [Permission.VIEW_REPORTS]: 'View Reports',
  [Permission.EXPORT_REPORTS]: 'Export Reports',
  [Permission.VIEW_EXPENSES]: 'View Expenses',
  [Permission.MANAGE_EXPENSES]: 'Manage Expenses',
  [Permission.DELETE_EXPENSES]: 'Delete Expenses',
  [Permission.VIEW_PROFIT_LOSS]: 'View Profit & Loss',
  [Permission.MANAGE_COST_SETTINGS]: 'Manage Cost Settings',
};